import _ from 'lodash';
import { buildUrl, checkGetResult } from '../../../../../shared/frontend/libs/utils';
import {
  commonApiService,
  makeApiServiceAsync,
  makeRetryableApiServiceAsync,
  PERFORM_DELETE,
  PERFORM_GET,
  PERFORM_PATCH,
  PERFORM_POST,
  PERFORM_PUT
} from '../../../../../shared/frontend/scripts/services/commonApiService';
import { networkService } from '../../../../../shared/frontend/scripts/services/networkService';
import {
  TAddonSettingsLoadResponse,
  TAddonSettingsSaveResponse,
  TAddonSettingsValues
} from '../../../../../shared/types/addonSettingsTypes';
import { IGetEventInsightsFiltersParams } from '../../../../../shared/types/eventTypes';
import { TGetSpellsListResponse, TGetSpellsParams } from '../../../../../shared/types/spellTypes';
import {
  IGetStatisticsDataQuery,
  ITierGearDataParams,
  ITierTalentsDataParams,
  ITierTrinketsDataParams,
  TGetStatisticsData,
  TGetTierTalentsData,
  TTierGearBySlotResponse,
  TTierTrinketsResponse
} from '../../../../../shared/types/statisticsTypes';
import { TKeystonePaceTargetMetric, TKeystonePaceTargetsResponse } from '../../../shared/types/keystonePace';
import {
  TSpecLoadoutTalentPovRunsResponse,
  TSpecLoadoutTalentRunsParams,
  TSpecLoadoutTalentRunsResponse
} from '../../../../../shared/types/specLoadoutTypes';
import { TCallback } from '../../../../../shared/types/utilityTypes';
import {
  IRequestCharacterClaimProcessBody,
  IStartInitialCharacterScanBody,
  IStartPendingCharacterClaimScanBody,
  IValidateCharacterClaimProcessBody
} from '../../../shared/types/claimCharacterTypes';
import { IGetGroupCompositionCompareParams } from '../../../shared/types/raidTypes';
import {
  ITimelineEventSummary,
  TCreateTimelineEvent,
  TGetTimelineEventListParams,
  TUpdateTimelineEvent
} from '../../../shared/types/timelineEventTypes';
import frontendLog from '../libs/frontendLog';
import {
  IReplaysOptions,
  TBossRankingsServerState,
  TCharacterDetailsServerState,
  TGuildDetailsServerState,
  TGuildRosterServerState,
  TGuildSnapshotDetailsServerState,
  TKeystoneCharacterRankingsServerState,
  TKeystoneDetailsServerState,
  TKeystoneRunAggregateState,
  TKeystoneTeamDetailsServerState,
  TKeystoneTeamRankingsServerState,
  TKeystoneWarbandRankingsServerState,
  TProgressRankingsServerState,
  TRaceProgressServerState,
  TRaidRankingsServerState,
  TRealmsServerState
} from '../libs/store';
import {
  IXsollaPricingResponse,
  TXsollaCancelSubscriptionPayload,
  TXsollaSubscriptionOperation
} from '../../../../../shared/common/xsolla';
import { IXsollaActivationStatusResponse } from '../../../../../shared/common/purchases';

export type TGetRaidInstanceRankingsParams = {
  realm?: string;
  raid: string;
  difficulty: string;
  region: string;
  page: number;
  faction?: string;
  guildsFilter?: string;
  partition?: string;
  limit?: number;
  recent?: boolean;
};

export type TReplayParams = {
  season: string;
  keystoneRunId: number;
};

export type TGetKeystonePaceTargetsParams = {
  season: string;
  zoneId: number;
  keyLevel: number;
  scope: string;
  metric: TKeystonePaceTargetMetric;
  itemLevel?: number;
};

export type TReplaySetOptionsParams = {
  keystoneReplaySetId: number;
  isActive: boolean;
};

export type TGetRaidLeaderboardWidgetDataParams = {
  realm?: string;
  raid: string;
  difficulty: string;
  region: string;
  page: number;
  faction?: string;
  guildsFilter?: string;
  limit?: number;
  recent?: boolean;
};

export type TGetKeystoneCharacterRankingsParams = {
  season: string;
  region: string;
  guild?: string;
  realm?: string;
  faction?: string;
  class: string;
  role: string;
  page: number;
  pageSize?: number;
};

export type TGetKeystoneWarbandRankingsParams = {
  season: string;
  region: string;
  role: string;
  page: number;
  pageSize?: number;
};

export type TGetKeystoneLoggedRunTournamentLiveParams = {
  season: string;
  filter: string;
  page: number;
};

export type TGetRaidLoggedEncounterLiveParams = {
  raid: string;
  filter: string;
  page: number;
  bosses: string;
  difficulty: string;
  region: string;
  guildIds: string;
};

export type TGetKeystoneSpecRankingsParams = {
  season: string;
  region: string;
  guild?: string;
  realm?: string;
  faction?: string;
  class: string;
  spec: string;
  page: number;
  pageSize?: number;
};

export type TGetGuildCharacterAchievementsParams = {
  region: string;
  guild?: string;
  realm?: string;
  tier?: string;
};

export type TGetMythicDungeonInvitationalData = {
  region: string;
};

export type TGetCollegiateDungeonRaceData = {
  bracket: string;
};

export type TGetMythicDungeonInvitational2018Data = {
  bracket: string;
};

export type TGetMythicDungeonTournamentData = {
  tournament: string;
  bracket: string;
};

export type TGetBossRankingsParams = {
  boss: string;
  raid: string;
  difficulty: string;
  region: string;
  page: number;
  faction?: string;
  realm?: string;
};

export type TGetProgressRankingsParams = {
  progress: number;
  raid: string;
  difficulty: string;
  region: string;
  page: number;
  faction?: string;
  realm?: string;
};

export type TVerifyAndActivate2fa = {
  token: string;
  password: string;
};

export type TDeactivate2fa = {
  password: string;
};

export type TGetRaceProgressParams = {
  raid: string;
  difficulty: string;
  region: string;
  realm?: string;
  faction?: string;
  includeLogData?: boolean;
  instanceId?: string;
};

export type TGetTeamLeaderboardParams = {
  region: string;
  season: string;
  page: number;
};

export type TGetTeamMythicPlusProgressParams = {
  region: string;
  team: string;
  season: string;
};

export type TGetTeamMythicPlusCalloutsParams = {
  region: string;
  team: string;
  season: string;
};

export type TGetTeamMythicPlusRanksParams = {
  region: string;
  team: string;
  season: string;
};

export type TGetTeamMythicPlusDungeonRunsParams = {
  region: string;
  team: string;
  season: string;
  dungeon: string;
};

export type TGetTeamRosterParams = {
  region: string;
  team: string;
};

export type TGetGuildDetailsParams = {
  region: string;
  realm: string;
  guild: string;
  expansion?: number;
};

export type TGetGuildSnapshotDetailsParams = {
  mode: string;
  id: number;
  timestamp: string;
};

export type TGetGuildRosterParams = {
  region: string;
  realm: string;
  guild: string;
};

export type TGetCombatLogSegmentsParams =
  import('../../../shared/types/combatLogDownloadTypes').TGetCombatLogSegmentsParams;
export type TPostCombatLogDownloadParams =
  import('../../../shared/types/combatLogDownloadTypes').TPostCombatLogDownloadParams;

export type TGetRealmsPageParams = {
  region: string;
  realm: string;
};

export type TGetRealmPageParams = {
  region: string;
  realm: string;
};

export type TGetGuildBossRankings = {
  region: string;
  realm: string;
  guild: string;
  raid: string;
  difficulty: string;
};

export type TGetCharacterBossRankings = {
  region: string;
  realm: string;
  character: string;
  raid: string;
  difficulty: string;
};

export type TGetGuildBossKillDetails = {
  region: string;
  realm: string;
  guild: string;
  raid: string;
  difficulty: string;
  boss: string;
};

export type TGetGuildBossAttempts = {
  region: string;
  realm: string;
  guild: string;
  raid: string;
  difficulty: string;
  boss: string;
  limit?: number;
};

export type TApplicationBody = {
  name?: string;
  intendedUse?: string;
  serviceUrl?: string;
};

export type TGetCharacterBaseParams = {
  region: string;
  realm: string;
  character: string;
};

export type TGetCharacterDetailsParams = TGetCharacterBaseParams & {
  season: string; // TEMP - remove this when we have the other parts loading on their own
  tier: string; // should follow season behavior probably
  loadout?: string;
};

export type TGetAccountCharactersParams = TGetCharacterBaseParams;

export type TGetCharacterLoadout = TGetCharacterBaseParams & {
  loadout: string;
};

export type TGetCharacterLoadoutsOptions = TGetAccountCharactersParams;

export type TGetCharacterSnapshotDetailsParams = {
  id: number;
  timestamp: string;
  mode: string;
};

export type TGetCharacterHistoryParams = {
  characterId: number;
};

export type TGetKeystoneTeamDetailsParams = {
  id?: string;
  platoonId?: number;
  season: string;
  eventId: number;
};

export type TGetCharacterMythicPlusRunsParams = {
  season: string;
  role: string;
  mode: string;
  affixes: string;
  date: string;
  characterId: number;
};

export type TGetKeystoneTeamMythicPlusRunsParams = {
  season: string;
  mode: string;
  affixes: string;
  date: string;
  teamId: number;
  eventId: number;
};

export type TGetKeystoneMythicPlusRunsParams = {
  season: string;
  platoonId: number;
  characterId: number;
  eventId: number;
  type: string;
  mode: string;
  affixes: string;
  date: string;
  numRuns: number;
  role: string;
};

export type TGetKeystoneRunAggregatesParams = {
  season: string;
  dungeon: string;
  class: string;
  role: string;
  affixes: string;
  minMythicLevel: number;
  maxMythicLevel: number;
  isTimed: boolean;
};

export type TGetKeystoneDungeonStatsParams = {
  season: string;
  affixes: string;
  minMythicLevel: number;
  maxMythicLevel: number;
};

export type TApplicationRequestParams = {
  message?: string;
  targetProfileId: number;
  sourceProfileId: number;
};

export type TGetEventStandingsParams = {
  eventSlug?: string | null;
  bracketSlug?: string | null;
  affixes?: string | null;
  teamId?: number | null;
  zoneId?: number | null;
  collectionId?: number | null;
  withCollectionPoints?: boolean;
  rankingsPage?: number;
};

export type TGetEventAnalyticsFiltersParams = {
  eventSlug?: string | null;
  bracketSlug?: string | null;
  collectionId?: number | null;
};

const apiService = {
  ...commonApiService,

  getQueueBatchInfo(batchId: string, callback: TCallback<any>) {
    const hostPath = '/api/crawler/monitor';
    const url = buildUrl(hostPath, { batchId });
    PERFORM_GET(url, callback);
  },

  /**
   * getRaidRaceTracker
   */
  getRaidRaceTracker(params: TGetRaidInstanceRankingsParams, callback: TCallback<TRaidRankingsServerState>) {
    const hostPath = '/api/raids/raid-race';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * regenerateApiKey
   */
  regenerateApiKey(apiKeyId: number, callback: TCallback<{}>) {
    const hostPath = `/api/applications/regenerate-api-key/${apiKeyId}`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback);
  },

  /**
   * generateApiKey
   */
  generateApiKey(appId: string, callback: TCallback<{}>) {
    const hostPath = `/api/applications/${appId}/api-keys/generate`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback);
  },

  /**
   * removeApiKey
   */
  removeApiKey(params: any, callback: TCallback<{}>) {
    const hostPath = `/api/applications/remove/${params.appId}/api-keys/${params.keyId}`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback);
  },

  /**
   * removeApplication
   */
  toggleBanApplication(appId: string, callback: TCallback<{}>) {
    const hostPath = `/api/applications/toggleBan/${appId}`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback);
  },

  /**
   * editApplication
   */
  editApplication(appId: string, params: any, callback: TCallback<{}>) {
    const hostPath = `/api/applications/edit/${appId}`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback);
  },

  /**
   * getApplicationsList
   */
  getApplicationsList(callback: TCallback<{}>) {
    const hostPath = '/api/applications';
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * getApplication
   */
  getApplication(id: string, callback: TCallback<{}>) {
    const hostPath = `/api/applications/${id}`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * createApplication
   */
  createApplication(params: TApplicationBody, callback: TCallback<{}>) {
    const hostPath = '/api/applications';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback);
  },

  /**
   * changeRateLimitApiKey
   */
  changeRateLimitApiKey(payload: { apiKeyId: number; perMinuteRateLimit: number }, callback: TCallback<{}>) {
    const hostPath = `/api/applications/change-limit/${payload.apiKeyId}`;
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, {
        perMinuteRateLimit: payload.perMinuteRateLimit
      })
      .then(
        (data) => callback(null, data),
        (err) => callback(err)
      );
  },

  /**
   * addReplay
   */
  addReplay(params: TReplayParams, callback: TCallback<{}>) {
    const hostPath = `/api/replays-management/add-replay/${params.season}/${params.keystoneRunId}`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback);
  },

  /**
   * removeReplay
   */
  removeReplay(params: TReplayParams, callback: TCallback<{}>) {
    const hostPath = `/api/replays-management/remove-replay/${params.season}/${params.keystoneRunId}`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback);
  },

  /**
   * getReplayList
   */
  getReplayList(params: any, callback: TCallback<{}>) {
    const hostPath = '/api/replays-management/replay-list';
    const url = buildUrl(hostPath, { region: params.region });
    PERFORM_GET(url, callback);
  },

  /**
   * getReplaysOptions
   */
  getReplaysOptions(callback: TCallback<{}>) {
    const hostPath = '/api/replays-management/options';
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * saveReplaysOptions
   */
  saveReplaysOptions(params: Partial<IReplaysOptions>, callback: TCallback<{}>) {
    const hostPath = '/api/replays-management/options';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback);
  },

  /**
   * saveReplaySetOptions
   */
  saveReplaySetOptions(params: TReplaySetOptionsParams, callback: TCallback<{}>) {
    const hostPath = '/api/replays-management/replay-set-options';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback);
  },

  /**
   * getRaidInstanceRankings
   */
  getRaidInstanceRankings(params: TGetRaidInstanceRankingsParams, callback: TCallback<TRaidRankingsServerState>) {
    const hostPath = '/api/raids/instance-rankings';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => checkGetResult<TRaidRankingsServerState>(data, callback),
      (err) => callback(err)
    );
  },

  /**
   * getRaidInstanceRankings
   */
  getRaidLeaderboardWidgetData(
    params: TGetRaidLeaderboardWidgetDataParams,
    callback: TCallback<TRaidRankingsServerState>
  ) {
    const hostPath = '/api/raids/raid-leaderboard-widget';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getKeystoneLoggedRunTournamentLive
   */
  getKeystoneLoggedRunTournamentLive(params: TGetKeystoneLoggedRunTournamentLiveParams, callback: TCallback<any>) {
    const hostPath = '/api/live-tracking/mythic-plus-runs';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getRaidLoggedEncounterLive
   */
  getRaidLoggedEncounterLive(params: TGetRaidLoggedEncounterLiveParams, callback: TCallback<any>) {
    const hostPath = '/api/live-tracking/raid-encounters';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getRaidLoggedEncounterDetails
   */
  getRaidLoggedEncounterDetails(params: any, callback: TCallback<any>) {
    const hostPath = `/api/raid/logged-encounters/${params.raid}/${params.id}`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * getKeystoneCharacterRankings
   */
  getKeystoneCharacterRankings(
    params: TGetKeystoneCharacterRankingsParams,
    callback: TCallback<TKeystoneCharacterRankingsServerState>
  ) {
    const hostPath = '/api/mythic-plus/rankings/characters';
    const url = buildUrl(
      hostPath,
      _.extend(
        {
          region: 'all'
        },
        params
      )
    );

    PERFORM_GET(url, callback);
  },

  /**
   * getKeystoneWarbandRankings
   */
  getKeystoneWarbandRankings(
    params: TGetKeystoneWarbandRankingsParams,
    callback: TCallback<TKeystoneWarbandRankingsServerState>
  ) {
    const hostPath = '/api/mythic-plus/rankings/warbands';
    const url = buildUrl(
      hostPath,
      _.extend(
        {
          region: 'all'
        },
        params
      )
    );

    PERFORM_GET(url, callback);
  },

  /**
   * getKeystoneSpecRankings
   */
  getKeystoneSpecRankings(
    params: TGetKeystoneSpecRankingsParams,
    callback: TCallback<TKeystoneCharacterRankingsServerState>
  ) {
    const hostPath = '/api/mythic-plus/rankings/specs';
    const url = buildUrl(
      hostPath,
      _.extend(
        {
          region: 'all'
        },
        params
      )
    );

    PERFORM_GET(url, callback);
  },

  /**
   * getGuildClientStatus
   */
  getGuildClientStatus(id: number, raidSlug: string, callback: TCallback<any>) {
    const hostPath = '/api/guilds/client-status';
    const url = buildUrl(hostPath, { id, raid: raidSlug });

    PERFORM_GET(url, callback);
  },

  /**
   * getGuildCharacterAchievements
   */
  getGuildCharacterAchievements(params: TGetGuildCharacterAchievementsParams, callback: TCallback<any>) {
    const hostPath = '/api/guilds/roster-achievements';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  /**
   * getMythicDungeonInvitationalData
   */
  getMythicDungeonInvitationalData(
    params: TGetMythicDungeonInvitationalData,
    callback: TCallback<TKeystoneTeamRankingsServerState>
  ) {
    const hostPath = '/api/mythic-dungeon-invitational/data';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  /**
   * getMythicDungeonInvitationalSummary
   */
  getMythicDungeonInvitationalSummary(callback: TCallback<TKeystoneTeamRankingsServerState>) {
    const hostPath = '/api/mythic-dungeon-invitational/summary';
    const url = buildUrl(hostPath, {});

    PERFORM_GET(url, callback);
  },

  /*
   * getMythicDungeonInvitationalLeaderboard
   */
  getMythicDungeonInvitationalLeaderboard(params: Object, callback: TCallback<TKeystoneTeamRankingsServerState>) {
    const hostPath = '/api/mythic-dungeon-invitational/leaderboard';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  /////
  /**
   * getMythicDungeonInvitational2018Data
   */
  getMythicDungeonInvitational2018Data(
    params: TGetMythicDungeonInvitational2018Data,
    callback: TCallback<TKeystoneTeamRankingsServerState>
  ) {
    const hostPath = '/api/mythic-dungeon-invitational-2018/data';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  /**
   * getMythicDungeonInvitational2018Summary
   */
  getMythicDungeonInvitational2018Summary(callback: TCallback<TKeystoneTeamRankingsServerState>) {
    const hostPath = '/api/mythic-dungeon-invitational-2018/summary';
    const url = buildUrl(hostPath, {});

    PERFORM_GET(url, callback);
  },

  /*
   * getMythicDungeonInvitational2018Leaderboard
   */
  getMythicDungeonInvitational2018Leaderboard(params: Object, callback: TCallback<TKeystoneTeamRankingsServerState>) {
    const hostPath = '/api/mythic-dungeon-invitational-2018/leaderboard';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  /*
   * getMythicDungeonInvitational2018Standings
   */
  getMythicDungeonInvitational2018Standings(params: Object, callback: TCallback<any>) {
    const hostPath = '/api/mythic-dungeon-invitational-2018/standings';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  /////

  /**
   * getMythicDungeonInvitationalAllStars2018Data
   */
  getMythicDungeonInvitationalAllStars2018Data(params: any, callback: TCallback<TKeystoneTeamRankingsServerState>) {
    const hostPath = '/api/mythic-dungeon-invitational-allstars-2018/data';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  /**
   * getMythicDungeonInvitationalAllStars2018Summary
   */
  getMythicDungeonInvitationalAllStars2018Summary(callback: TCallback<TKeystoneTeamRankingsServerState>) {
    const hostPath = '/api/mythic-dungeon-invitational-allstars-2018/summary';
    const url = buildUrl(hostPath, {});

    PERFORM_GET(url, callback);
  },

  /*
   * getMythicDungeonInvitationalAllStars2018Leaderboard
   */
  getMythicDungeonInvitationalAllStars2018Leaderboard(
    params: Object,
    callback: TCallback<TKeystoneTeamRankingsServerState>
  ) {
    const hostPath = '/api/mythic-dungeon-invitational-allstars-2018/leaderboard';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  /*
   * getMythicDungeonInvitationalAllStars2018Standings
   */
  getMythicDungeonInvitationalAllStars2018Standings(params: Object, callback: TCallback<any>) {
    const hostPath = '/api/mythic-dungeon-invitational-allstars-2018/standings';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  /////
  /**
   * getMythicDungeonTournamentData
   */
  getMythicDungeonTournamentData(
    params: TGetMythicDungeonInvitational2018Data,
    callback: TCallback<TKeystoneTeamRankingsServerState>
  ) {
    const hostPath = '/api/mythic-dungeon-tournament/data';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  /**
   * getMythicDungeonTournamentSummary
   */
  getMythicDungeonTournamentSummary(params: any, callback: TCallback<TKeystoneTeamRankingsServerState>) {
    const hostPath = '/api/mythic-dungeon-tournament/summary';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  /*
   * getMythicDungeonTournamentLeaderboard
   */
  getMythicDungeonTournamentLeaderboard(params: Object, callback: TCallback<TKeystoneTeamRankingsServerState>) {
    const hostPath = '/api/mythic-dungeon-tournament/leaderboard';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  /*
   * getMythicDungeonTournamentStandings
   */
  getMythicDungeonTournamentStandings(params: Object, callback: TCallback<any>) {
    const hostPath = '/api/mythic-dungeon-tournament/standings';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  /////

  /**
   * getCollegiateDungeonRaceData
   */
  getCollegiateDungeonRaceData(
    params: TGetCollegiateDungeonRaceData,
    callback: TCallback<TKeystoneTeamRankingsServerState>
  ) {
    const hostPath = '/api/collegiate-dungeon-race/data';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  /**
   * getCollegiateDungeonRaceSummary
   */
  getCollegiateDungeonRaceSummary(callback: TCallback<TKeystoneTeamRankingsServerState>) {
    const hostPath = '/api/collegiate-dungeon-race/summary';
    const url = buildUrl(hostPath, {});

    PERFORM_GET(url, callback);
  },

  /*
   * getCollegiateDungeonRaceLeaderboard
   */
  getCollegiateDungeonRaceLeaderboard(params: Object, callback: TCallback<TKeystoneTeamRankingsServerState>) {
    const hostPath = '/api/collegiate-dungeon-race/leaderboard';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  /**
   * getKeystoneRunAggregates
   */
  getKeystoneRunAggregates(params: TGetKeystoneRunAggregatesParams, callback: TCallback<TKeystoneRunAggregateState>) {
    const hostPath = '/api/mythic-plus/runs/aggregates';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getKeystoneDungeonStats
   */
  getKeystoneDungeonStats(params: TGetKeystoneDungeonStatsParams, callback: TCallback<TKeystoneRunAggregateState>) {
    const hostPath = '/api/mythic-plus/dungeon-stats';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getBossRankings
   */
  getBossRankings(params: TGetBossRankingsParams, callback: TCallback<TBossRankingsServerState>) {
    const hostPath = '/api/raids/boss-rankings';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getProgressRankings
   */
  getProgressRankings(params: TGetProgressRankingsParams, callback: TCallback<TProgressRankingsServerState>) {
    const hostPath = '/api/raids/progress-rankings';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * addGuild
   */
  addGuild(params: any, callback: TCallback<any>) {
    const hostPath = '/api/crawler/guilds';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'addGuild request error');
        callback(err);
      });
  },

  /**
   * login
   */
  login(params: any, callback: TCallback<any>) {
    const hostPath = '/api/auth/login';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'login request error');
        callback(err);
      });
  },

  /**
   * forgotPassword
   */
  forgotPassword(params: any, callback: TCallback<any>) {
    const hostPath = '/api/auth/forgot';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'forgot password request error');
        callback(err);
      });
  },

  /**
   * resetPassword
   */
  resetPassword(params: any, callback: TCallback<any>) {
    const hostPath = '/api/auth/reset';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'resetPassword request error');
        callback(err);
      });
  },

  /**
   * sendAddressVerificationEmail
   */
  sendAddressVerificationEmail(params: any, callback: TCallback<any>) {
    const hostPath = '/api/auth/login/resend';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'sendAddressVerificationEmail request error');
        callback(err);
      });
  },

  /**
   * register
   */
  register(params: any, callback: TCallback<any>) {
    const hostPath = '/api/register';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'register request error');
        callback(err);
      });
  },

  /**
   * Generate Two Factor Authentication
   */
  generate2FA(callback: TCallback<any>) {
    const hostPath = '/api/auth/2fa/generate';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'generate2FA request error');
      callback(err);
    });
  },

  /**
   * Generate Two Factor Authentication
   */
  regenerate2faToken(callback: TCallback<any>) {
    const hostPath = '/api/auth/2fa/regenerate';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'regenerate2faToken request error');
      callback(err);
    });
  },

  /**
   * Activate Two Factor Authentication
   */
  verifyAndActivate2faAuth(params: TVerifyAndActivate2fa, callback: TCallback<any>) {
    const hostPath = '/api/auth/2fa/activate';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params as any)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'verifyAndActivate2faAuth request error');
        callback(err);
      });
  },

  /**
   * Deactivate Two Factor Authentication
   */
  deactivate2faAuth(params: TDeactivate2fa, callback: TCallback<any>) {
    const hostPath = '/api/auth/2fa/deactivate';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params as any)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'deactivate2faAuth request error');
        callback(err);
      });
  },

  /**
   * Verify Two Factor Authentication
   */
  verify2FA(params: any, callback: TCallback<any>) {
    const hostPath = '/api/auth/2fa/verify';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'verify2FA request error');
        callback(err);
      });
  },

  /**
   * agreeToPolicies
   */
  agreeToPolicies(callback: TCallback<any>) {
    const hostPath = '/api/agree-to-policies';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'agreeToPolicies request error');
      callback(err);
    });
  },

  /**
   * addCharacter
   */
  addCharacter(params: any, callback: TCallback<any>) {
    const hostPath = '/api/crawler/characters';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'addCharacter request error');
        callback(err);
      });
  },

  /**
   * addKeystoneDungeon
   */
  addKeystoneDungeon(params: any, callback: TCallback<any>) {
    const hostPath = '/api/crawler/dungeons';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'addKeystoneDungeon request error');
        callback(err);
      });
  },

  /**
   * getRaceProgress
   */
  getRaceProgress(params: TGetRaceProgressParams, callback: TCallback<TRaceProgressServerState>) {
    const hostPath = '/api/hall-of-fame';
    const queryParams = params;

    const url = buildUrl(hostPath, queryParams);
    PERFORM_GET(url, callback);
  },

  /**
   * getGuildDetails
   */
  getGuildDetails(params: TGetGuildDetailsParams, callback: TCallback<TGuildDetailsServerState>) {
    const hostPath = '/api/guilds/details';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * Get Guilds by IDs
   */
  getGuildsByIds(ids: string[], callback: TCallback<{ name: string; id: number }[]>) {
    const hostPath = '/api/guilds/get-by-ids';
    const url = buildUrl(hostPath, { ids: ids.join(',') });
    PERFORM_GET(url, callback);
  },
  /**
   * getGuildSnapshotDetails
   */
  getGuildSnapshotDetails(
    params: TGetGuildSnapshotDetailsParams,
    callback: TCallback<TGuildSnapshotDetailsServerState>
  ) {
    const hostPath = `/api/guilds/snapshots/${params.mode}/${params.id}/${params.timestamp}`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * getGuildRoster
   */
  getGuildRoster(params: TGetGuildRosterParams, callback: TCallback<TGuildRosterServerState>) {
    const hostPath = '/api/guilds/roster';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getGuildRaidRankings
   */
  getGuildRaidRankings(params: TGetGuildBossRankings, callback: TCallback<TGuildDetailsServerState>) {
    const hostPath = '/api/guilds/raid-rankings';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getGuildBossKillDetails
   */
  getGuildBossKillDetails(params: TGetGuildBossKillDetails, callback: TCallback<TGuildDetailsServerState>) {
    const hostPath = '/api/guilds/boss-kills';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getGuildBossAttempts
   */
  getGuildBossAttempts(params: TGetGuildBossAttempts, callback: TCallback<any>) {
    const hostPath = '/api/guilds/boss-attempts';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getTeamLeaderboard
   */
  getTeamLeaderboard(params: TGetTeamLeaderboardParams, callback: TCallback<TGuildDetailsServerState>) {
    const hostPath = '/api/teams/leaderboard';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getTeamRoster
   */
  getTeamRoster(params: TGetTeamRosterParams, callback: TCallback<TGuildRosterServerState>) {
    const hostPath = '/api/teams/roster';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getGroupHistory
   */
  getGroupHistory(groupId: number, callback: TCallback<any>) {
    const hostPath = `/api/groups/${groupId}/activities`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * getTeamMythicPlusProgress
   */
  getTeamMythicPlusProgress(params: TGetTeamMythicPlusProgressParams, callback: TCallback<TGuildDetailsServerState>) {
    const hostPath = '/api/teams/mythic-plus/progress';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getTeamMythicPlusCallouts
   */
  getTeamMythicPlusCallouts(params: TGetTeamMythicPlusCalloutsParams, callback: TCallback<TGuildDetailsServerState>) {
    const hostPath = '/api/teams/mythic-plus/callouts';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getTeamMythicPlusRanks
   */
  getTeamMythicPlusRanks(params: TGetTeamMythicPlusRanksParams, callback: TCallback<TGuildDetailsServerState>) {
    const hostPath = '/api/teams/mythic-plus/ranks';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getTeamMythicPlusDungeonRuns
   */
  getTeamMythicPlusDungeonRuns(
    params: TGetTeamMythicPlusDungeonRunsParams,
    callback: TCallback<TGuildDetailsServerState>
  ) {
    const hostPath = '/api/teams/mythic-plus/dungeon-runs';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getKeystoneTeamDetails
   */
  getKeystoneTeamDetails(params: TGetKeystoneTeamDetailsParams, callback: TCallback<TKeystoneTeamDetailsServerState>) {
    const hostPath = '/api/mythic-plus-teams/details';

    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getCharacterDetails
   */
  getCharacterDetails(params: TGetCharacterDetailsParams, callback: TCallback<TCharacterDetailsServerState>) {
    const hostPath = `/api/characters/${params.region}/${params.realm}/${params.character}`;
    const queryParams = {
      season: params.season,
      tier: params.tier,
      loadout: params.loadout
    };

    const url = buildUrl(hostPath, queryParams);
    PERFORM_GET(url, callback);
  },

  // NOTE: getCharacterLoadouts is defined in commonApiService.ts

  /**
   * getAccountCharacters
   */
  getAccountCharacters(params: TGetAccountCharactersParams, callback: TCallback<any>) {
    const hostPath = `/api/characters/${params.region}/${params.realm}/${params.character}/alts`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * getCharacterSnapshotDetails
   */
  getCharacterSnapshotDetails(
    params: TGetCharacterSnapshotDetailsParams,
    callback: TCallback<TCharacterDetailsServerState>
  ) {
    const hostPath = `/api/characters/snapshots/${params.mode}/${params.id}/${params.timestamp}`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * getCharacterHistory
   */
  getCharacterHistory(params: TGetCharacterHistoryParams, callback: TCallback<TCharacterDetailsServerState>) {
    const hostPath = '/api/characters/persona-history';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getCharacterMythicPlusRuns
   */
  getCharacterMythicPlusRuns(
    params: TGetCharacterMythicPlusRunsParams,
    callback: TCallback<TCharacterDetailsServerState>
  ) {
    const hostPath = '/api/characters/mythic-plus-scored-runs';

    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getCharacterRaidProgress
   */
  getCharacterRaidProgress(params: Omit<TGetCharacterDetailsParams, 'season'>, callback: TCallback<any>) {
    const hostPath = `/api/characters/${params.region}/${params.realm}/${params.character}/raid-progress`;
    const queryParams = {
      tier: params.tier
    };

    const url = buildUrl(hostPath, queryParams);
    PERFORM_GET(url, callback);
  },

  /**
   * getCharacterRaidProgress
   */
  getCharacterLoadout(params: Omit<TGetCharacterDetailsParams, 'season'>, callback: TCallback<any>) {
    const hostPath = `/api/characters/${params.region}/${params.realm}/${params.character}/raid-progress`;
    const queryParams = {
      tier: params.tier
    };

    const url = buildUrl(hostPath, queryParams);
    PERFORM_GET(url, callback);
  },

  /**
   * getCharacterMythicPlusProgress
   */
  getCharacterMythicPlusProgress(params: Omit<TGetCharacterDetailsParams, 'tier'>, callback: TCallback<any>) {
    const hostPath = `/api/characters/${params.region}/${params.realm}/${params.character}/mythic-plus-progress`;
    const queryParams = {
      season: params.season
    };

    const url = buildUrl(hostPath, queryParams);
    PERFORM_GET(url, callback);
  },

  /**
   * getKeystoneMythicPlusRuns
   */
  getKeystoneMythicPlusRuns(
    params: TGetKeystoneMythicPlusRunsParams,
    callback: TCallback<TKeystoneDetailsServerState>
  ) {
    const hostPath = '/api/mythic-plus/dungeons';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  getKeystonePaceTargets(params: TGetKeystonePaceTargetsParams, callback: TCallback<TKeystonePaceTargetsResponse>) {
    const url = buildUrl('/api/mythic-plus/pace-targets', params);
    PERFORM_GET(url, callback);
  },

  /**
   * getKeystoneTeamMythicPlusRuns
   */
  getKeystoneTeamMythicPlusRuns(
    params: TGetKeystoneTeamMythicPlusRunsParams,
    callback: TCallback<TKeystoneTeamDetailsServerState>
  ) {
    const hostPath = '/api/mythic-plus-teams/runs';

    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getKeystoneTeamSimilarMythicPlusRuns
   */
  getKeystoneTeamSimilarMythicPlusRuns(params: any, callback: TCallback<TKeystoneTeamDetailsServerState>) {
    const hostPath = '/api/mythic-plus-teams/runs-similar';

    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getCharacterRaidRankings
   */
  getCharacterRaidRankings(params: TGetCharacterBossRankings, callback: TCallback<TCharacterDetailsServerState>) {
    const hostPath = `/api/characters/${params.region}/${params.realm}/${params.character}/raids/${params.raid}/${params.difficulty}`;
    const queryParams = {};

    const url = buildUrl(hostPath, queryParams);
    PERFORM_GET(url, callback);
  },

  /**
   * getKeystoneRunDetails
   */
  getKeystoneRunDetails(params: any, callback: TCallback<any>) {
    const hostPath = `/api/mythic-plus/runs/${params.season}/${params.id}`;
    const queryParams = {};

    const url = buildUrl(hostPath, queryParams);
    PERFORM_GET(url, callback);
  },

  /**
   * getKeystoneLoggedRunDetails
   */
  getKeystoneLoggedRunDetails(params: any, callback: TCallback<any>) {
    const hostPath = `/api/mythic-plus/logged-runs/${params.season}/${params.id}`;
    const queryParams = {};

    const url = buildUrl(hostPath, queryParams);
    PERFORM_GET(url, callback);
  },

  getCombatLogSegments(params: TGetCombatLogSegmentsParams, callback: TCallback<any>) {
    const hostPath = '/admin/api/combatlog/segments';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  postCombatLogDownload(params: TPostCombatLogDownloadParams, callback: TCallback<any>) {
    const hostPath = '/admin/api/combatlog/download';
    const url = buildUrl(hostPath, {});

    PERFORM_POST(url, params, callback);
  },

  /**
   * getCharacterKeystoneRuns
   */
  getCharacterKeystoneRuns(params: any, callback: TCallback<any>) {
    const hostPath = '/api/characters/mythic-plus-runs';

    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getKeystoneTeamKeystoneRunsByDungeon
   */
  getKeystoneTeamKeystoneRunsByDungeon(params: any, callback: TCallback<any>) {
    const hostPath = '/api/mythic-plus-teams/runs-by-dungeon';
    const queryParams = params;

    const url = buildUrl(hostPath, queryParams);
    PERFORM_GET(url, callback);
  },

  /**
   * getConnectedRealms
   */
  getConnectedRealms(params: TGetRealmsPageParams, callback: TCallback<TRealmsServerState>) {
    const url = buildUrl('/api/connected-realms', params);
    PERFORM_GET(url, callback);
  },

  /**
   * getMythicPlusRealms
   */
  getMythicPlusRealms(params: any, callback: TCallback<TRealmsServerState>) {
    const url = buildUrl('/api/v1/mythic-plus/leaderboard-capacity', params);
    PERFORM_GET(url, callback);
  },

  /**
   * getMythicPlusCutoffs
   */
  getMythicPlusCutoffs(params: any, callback: TCallback<TRealmsServerState>) {
    const url = buildUrl('/api/v1/mythic-plus/season-cutoffs', params);
    PERFORM_GET(url, callback);
  },

  /**
   * getUserBattleNetSettings
   */
  getUserBattleNetSettings(callback: TCallback<any>) {
    const hostPath = '/api/settings/battlenet';
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * getEventStandings
   */
  getEventStandings(params: TGetEventStandingsParams, callback: TCallback<any>) {
    const hostPath = '/api/events/standings';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getEventStandingsFilters
   */
  getEventStandingsFilters(params: any, callback: TCallback<any>) {
    const hostPath = '/api/events/standings/filters';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getEventAnalyticsFilters
   */
  getEventAnalyticsFilters(params: IGetEventInsightsFiltersParams, callback: TCallback<any>) {
    const hostPath = '/api/events/insights/filters';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getEventRegistrationData
   */
  getEventRegistrationData(params: any, callback: TCallback<any>) {
    const hostPath = '/api/events/register';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * saveEventRegistrationData
   */
  saveEventRegistrationData(params: any, callback: TCallback<any>) {
    const hostPath = '/api/events/register';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback);
  },

  /**
   * revokeBattleNetAuth
   *
   * TODO: FIX XSS potential here
   */
  revokeBattleNetAuth(callback: TCallback<any>) {
    const hostPath = '/battlenet/revoke';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'revokeBattleNetAuth request error');
      callback(err);
    });
  },

  /**
   * revokePatreonAuth
   *
   * TODO: FIX XSS potential here
   */
  revokePatreonAuth(callback: TCallback<any>) {
    const hostPath = '/patreon/revoke';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'revokePatreonAuth request error');
      callback(err);
    });
  },

  /**
   * getUserTimeSettings
   */
  getUserTimeSettings(callback: TCallback<any>) {
    const hostPath = '/api/settings/time';
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * getUserProfileSettings
   */
  getUserProfileSettings(callback: TCallback<any>) {
    const hostPath = '/api/settings/profile';
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * getBanList
   */
  getBanList(params: { userId?: number; groupId?: number }, callback: TCallback<any>) {
    const hostPath = '/api/settings/block-list';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * addBanEntry
   */
  addBanEntry(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/block-list/add';
    const url = buildUrl(hostPath, {});

    networkService
      .post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'addBanEntry request error');
        callback(err);
      });
  },

  /**
   * deleteBanEntry
   */
  deleteBanEntry(entryId: number, callback: TCallback<any>) {
    const hostPath = '/api/settings/block-list/delete';
    const url = buildUrl(hostPath, { id: entryId });

    PERFORM_POST(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'deleteBanEntry request error');
      callback(err);
    });
  },

  /**
   * getUserSocialSettings
   */
  getUserSocialSettings(callback: TCallback<any>) {
    const hostPath = '/api/settings/social';
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * getUserNotificationSettings
   */
  getUserNotificationSettings(callback: TCallback<any>) {
    const hostPath = '/api/settings/notifications';
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * getUserPatreonSettings
   */
  getUserPatreonSettings(callback: TCallback<any>) {
    const hostPath = '/api/settings/patreon';
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * getUserSubscriptionSettings
   * Returns unified subscription data from all sources (Patreon, Xsolla, Internal)
   */
  getUserSubscriptionSettings(callback: TCallback<any>) {
    const hostPath = '/api/settings/subscriptions';
    const url = buildUrl(hostPath, {});
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * createXsollaPaymentToken
   * Creates a payment token for Xsolla Pay Station
   */
  createXsollaPaymentToken(
    params:
      | {
          purchaseType?: 'subscription';
          tier: 'revered' | 'exalted' | 'illustrious';
          isAnnual?: boolean;
          operation?: TXsollaSubscriptionOperation;
          market?: string;
          language?: string;
          paymentMethod?: 'alipay' | 'wechat';
          returnUrl?: string;
          checkoutAttemptId?: string;
          gaClientId?: string;
          gaSessionId?: string;
          sku?: never;
        }
      | {
          purchaseType: 'inventory';
          sku: string;
          market?: string;
          language?: string;
          paymentMethod?: 'alipay' | 'wechat';
          returnUrl?: string;
          checkoutAttemptId?: string;
          gaClientId?: string;
          gaSessionId?: string;
          tier?: never;
          isAnnual?: never;
        },
    callback: TCallback<{ success: boolean; token: string; orderId?: string; paymentExternalId?: string }>
  ) {
    const hostPath = '/api/xsolla/create-token';
    const url = buildUrl(hostPath, {});
    networkService.post(url, params).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getXsollaPricing
   * Returns display-ready pricing for Xsolla subscription tiers.
   */
  getXsollaPricing(params: { market?: string }, callback: TCallback<IXsollaPricingResponse & { success: boolean }>) {
    const hostPath = '/api/xsolla/pricing';
    const url = buildUrl(hostPath, params || {});
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getAllSubscriptions
   * Gets all subscriptions across all providers (unified view)
   */
  getAllSubscriptions(callback: TCallback<any>) {
    const hostPath = '/api/purchases/subscriptions';
    const url = buildUrl(hostPath, {});
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * recoverPurchases
   * Attempts to recover orphaned purchases across all providers
   */
  recoverPurchases(callback: TCallback<{ success: boolean; recovered: number; message: string; byProvider: any }>) {
    const hostPath = '/api/purchases/recover';
    const url = buildUrl(hostPath, {});
    networkService.post(url, {}).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * getXsollaActivationStatus
   * Attempts checkout recovery and returns a narrow Xsolla activation status.
   */
  getXsollaActivationStatus(
    params: { attemptRecovery?: boolean },
    callback: TCallback<IXsollaActivationStatusResponse>
  ) {
    const hostPath = '/api/purchases/xsolla/activation-status';
    const url = buildUrl(hostPath, {});
    networkService.post(url, params || {}).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * createXsollaSubscriberAccountLink
   * Creates a hosted Xsolla Subscriber Account link for subscription management.
   */
  createXsollaSubscriberAccountLink(callback: TCallback<{ success: boolean; redirectUrl: string }>) {
    const hostPath = '/api/purchases/xsolla/subscriber-account';
    const url = buildUrl(hostPath, {});
    networkService.post(url, {}).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * cancelSubscription
   * Cancels a subscription via the unified purchases API
   */
  cancelSubscription(
    subscriptionId: string,
    payloadOrCallback: TXsollaCancelSubscriptionPayload | TCallback<{ success: boolean; message: string }> | undefined,
    callback?: TCallback<{ success: boolean; message: string }>
  ) {
    const payload = typeof payloadOrCallback === 'function' ? undefined : payloadOrCallback;
    const resolvedCallback = typeof payloadOrCallback === 'function' ? payloadOrCallback : callback;
    const hostPath = `/api/purchases/subscriptions/${subscriptionId}/cancel`;
    const url = buildUrl(hostPath, {});
    networkService.post(url, payload || {}).then(
      (data) => resolvedCallback?.(null, data),
      (err) => resolvedCallback?.(err)
    );
  },

  /**
   * getUserVanityAliasSettings
   */
  getUserVanityAliasSettings(callback: TCallback<any>) {
    const hostPath = '/api/settings/vanity-alias';
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * getUserAddonSettings
   */
  getUserAddonSettings(callback: TCallback<TAddonSettingsLoadResponse>) {
    const hostPath = '/api/settings/addon';
    const url = buildUrl(hostPath, {});
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * saveUserAddonSettings
   */
  saveUserAddonSettings(params: TAddonSettingsValues, callback: TCallback<TAddonSettingsSaveResponse>) {
    const hostPath = '/api/settings/addon';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback);
  },

  /**
   * getUserEmailSettings
   */
  getUserEmailSettings(callback: TCallback<any>) {
    const hostPath = '/api/settings/email';
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * resendEmailChangeVerification
   */
  resendEmailChangeVerification(callback: TCallback<any>) {
    const hostPath = '/api/settings/email/resend';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'resendEmailChangeVerification request error');
      callback(err);
    });
  },

  /**
   * saveUserEmailSettings
   */
  saveUserEmailSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/email';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'saveUserEmailSettings request error');
        callback(err);
      });
  },

  /**
   * saveUserPasswordSettings
   */
  saveUserPasswordSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/password';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'saveUserPasswordSettings request error');
        callback(err);
      });
  },

  /**
   * saveDeleteAccountSettings
   */
  saveDeleteAccountSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/delete-account';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'saveDeleteAccountSettings request error');
        callback(err);
      });
  },

  /**
   * saveUserUsernameSettings
   */
  saveUserUsernameSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/username';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'saveUserUsernameSettings request error');
        callback(err);
      });
  },

  /**
   * saveUserSocialSettings
   */
  saveUserSocialSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/social';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'saveUserSocialSettings request error');
        callback(err);
      });
  },

  /**
   * userRemoveConnection
   */
  userRemoveConnection(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/oauth/remove-connection';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'userRemoveConnection request error');
        callback(err);
      });
  },

  /**
   * saveUserNotificationSettings
   */
  saveUserNotificationSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/notifications';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'saveUserNotificationSettings request error');
        callback(err);
      });
  },

  /**
   * saveUserPatreonSettings
   */
  saveUserPatreonSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/patreon';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'saveUserPatreonSettings request error');
        callback(err);
      });
  },

  /**
   * addUserVanityAlias
   */
  addUserVanityAlias(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/vanity-alias/add';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'addUserVanityAlias request error');
        callback(err);
      });
  },

  /**
   * deleteUserVanityAlias
   */
  deleteUserVanityAlias(id: number, callback: TCallback<any>) {
    const hostPath = '/api/settings/vanity-alias/delete';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, { id } as any)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'deleteUserVanityAlias request error');
        callback(err);
      });
  },

  /**
   * saveUserProfileSettings
   */
  saveUserProfileSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/profile';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'saveUserProfileSettings request error');
        callback(err);
      });
  },

  /**
   * getCharacterPrivacySettings
   */
  getCharacterPrivacySettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/characters/privacy';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getCharacterRecruitmentSettings
   */
  getCharacterRecruitmentSettings(characterId: number, callback: TCallback<any>) {
    const hostPath = `/api/recruitment/characters/${characterId}`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  // fetch all recruitment profiles for specific target
  getTargetRecruitmentProfiles(entityType: string, targetId: number, callback: TCallback<any>) {
    const hostPath = '/api/recruitment/target-profiles';
    const url = buildUrl(hostPath, { entity_type: entityType, target_id: targetId });
    PERFORM_GET(url, callback);
  },

  // fetch all recruitment profiles
  getOwnRecruitmentProfiles(callback: TCallback<any>) {
    const hostPath = '/api/recruitment/own-profiles';
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  getFeaturedRecruitmentProfiles(queryParams: any, callback: TCallback<any>) {
    const hostPath = '/api/recruitment/featured-profiles';
    const url = buildUrl(hostPath, queryParams);
    PERFORM_GET(url, callback);
  },

  getRecentRecruitmentProfiles(queryParams: any, callback: TCallback<any>) {
    const hostPath = '/api/recruitment/recent-profiles';
    const url = buildUrl(hostPath, queryParams);
    PERFORM_GET(url, callback);
  },

  /**
   * fetching recruitment profiles
   */
  getStaticRecruitmenProfileData(profileId: number, callback: TCallback<any>) {
    const hostPath = `/api/recruitment/profiles/${profileId}/static`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },
  getRecruitmentProfile(profileId: number, callback: TCallback<any>) {
    const hostPath = `/api/recruitment/profiles/${profileId}`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },
  getProfilesForApplication(targetProfileId: number, callback: TCallback<any>) {
    const hostPath = '/api/recruitment/check-requirements';
    const url = buildUrl(hostPath, { targetProfileId });
    PERFORM_GET(url, callback);
  },

  createRecruitmentProfile(params: any, callback: TCallback<any>) {
    const hostPath = '/api/recruitment/profiles';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'createRecruitmentProfile request error');
        callback(err);
      });
  },

  saveCharacterRecruitmentProfile(params: any, callback: TCallback<any>) {
    const hostPath = `/api/recruitment/profiles/${params.id}`;
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'saveCharacterRecruitmentProfile request error');
        callback(err);
      });
  },

  setRecruitmentProfilePublished(params: any, callback: TCallback<any>) {
    const hostPath = `/api/recruitment/profiles/${params.profileId}/publish`;
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, { publish: params.publish } as any)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'setRecruitmentProfilePublished request error');
        callback(err);
      });
  },

  // delete recruitment profile
  deleteRecruitmentProfile(profileId: number, callback: TCallback<any>) {
    const hostPath = `/api/recruitment/profiles/${profileId}`;
    const url = buildUrl(hostPath, {});
    PERFORM_DELETE(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'deleteRecruitmentProfile request error');
      callback(err);
    });
  },

  // NOTE: saveCharacterPrivacySettings is defined in commonApiService.ts

  /**
   * getGroupClientUsage
   */
  getGroupClientUsage(params: { group_id: number; token: string }, callback: TCallback<any>) {
    const hostPath = '/api/settings/groups/client-usage';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getKeystoneEventClientUsage
   */
  getKeystoneEventClientUsage(params: { event_id: number; token: string }, callback: TCallback<any>) {
    const hostPath = '/api/keystone-event/client-usage';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getGroupRoleSettings
   */
  getGroupRoleSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/groups/roles';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * addGroupRole
   */
  addGroupRole(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/groups/roles';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'addGroupRole request error');
        callback(err);
      });
  },

  /**
   * removeGroupRole
   */
  removeGroupRole(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/groups/roles';
    const url = buildUrl(hostPath, {});
    networkService
      .delete(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'removeGroupRole request error');
        callback(err);
      });
  },

  /**
   * getArticleContent
   */
  getArticleContent(params: any, callback: TCallback<any>) {
    const hostPath = '/api/news/article';
    const queryParams = { id: params.id };

    const url = buildUrl(hostPath, queryParams);
    PERFORM_GET(url, callback);
  },

  /**
   * getArticlesWeeklyRoute
   */
  getArticlesWeeklyRoute(callback: TCallback<any>) {
    const hostPath = '/api/news/weekly-routes';
    const url = buildUrl(hostPath, {});

    PERFORM_GET(url, callback);
  },

  /**
   * getArticlesPlayLikeThePros
   */
  getArticlesPlayLikeThePros(callback: TCallback<any>) {
    const hostPath = '/api/news/play-like-the-pros';
    const url = buildUrl(hostPath, {});

    PERFORM_GET(url, callback);
  },

  /**
   * getEmbeddedPlayerStream
   */
  getEmbeddedPlayerStream(params: any, callback: TCallback<any>) {
    const hostPath = '/api/streamers/active';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  getFeaturedStreamers(params: any, callback: TCallback<any>) {
    const hostPath = '/api/streamers/featured';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * mergeCharacterInstances
   */
  mergeCharacterInstances(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/characters/transfer';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, params)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'mergeCharacterInstances request error');
        callback(err);
      });
  },

  /**
   * createReview
   */
  createReview(reviewSlug: string, callback: TCallback<any>) {
    const hostPath = '/api/in-review/create';
    const url = buildUrl(hostPath, { reviewSlug });
    PERFORM_POST(url, {}, callback);
  },

  /**
   * getReviewJobStatus
   */
  getReviewJobStatus(reviewSlug: string, callback: TCallback<any>) {
    const hostPath = '/api/in-review/monitor-job';
    const url = buildUrl(hostPath, { reviewSlug });
    PERFORM_GET(url, callback);
  },

  /**
   * deleteReview
   */
  deleteReview(reviewSlug: string, callback: TCallback<any>) {
    const hostPath = '/api/in-review/delete';
    const url = buildUrl(hostPath, { reviewSlug });
    PERFORM_POST(url, {}, callback);
  },

  /**
   * openReview
   */
  openReview(params: { id: number; reviewSlug: string }, callback: TCallback<any>) {
    const hostPath = '/api/in-review/open';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getReviewStatsTokens
   */
  getReviewStatsTokens(params: { id: number; reviewSlug: string; type: string }, callback: TCallback<any>) {
    const hostPath = '/api/in-review/stats-tokens';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * viewUserProfile
   */
  viewUserProfile(params: any, callback: TCallback<any>) {
    const hostPath = '/api/user/view-profile';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * viewUserCharacters
   */
  viewUserCharacters(params: any, callback: TCallback<any>) {
    const hostPath = '/api/user/view-characters';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getRandomIcon
   */
  getRandomIcon(callback: TCallback<any>) {
    const hostPath = '/api/random-icon';
    const url = buildUrl(hostPath, {});
    networkService
      .get(url)
      .then((data) => callback(null, data))
      .catch((err) => {
        frontendLog.error({ err }, 'getRandomIcon request error');
        callback(err);
      });
  },

  requestApplication(params: TApplicationRequestParams, callback: TCallback<any>) {
    const hostPath = '/api/recruitment/application/create';
    const url = buildUrl(hostPath, params);
    PERFORM_POST(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'requestApplication request error');
      callback(err);
    });
  },

  viewLiveTrackingBossProgress(params: any, callback: TCallback<any>) {
    const hostPath = '/api/v1/live-tracking/bossprogress';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  viewLiveTrackingRaidProgress(params: any, callback: TCallback<any>) {
    const hostPath = '/api/v1/live-tracking/raidprogress';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  viewLiveTrackingGuildLoot(params: any, callback: TCallback<any>) {
    const hostPath = '/api/v1/live-tracking/guildloot';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  viewLiveTrackingRaidPulls(params: any, callback: TCallback<any>) {
    const hostPath = '/api/v1/live-tracking/raidpulls';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  viewLiveTrackingRaidComps(params: any, callback: TCallback<any>) {
    const hostPath = '/api/v1/live-tracking/raidcomps';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  viewLiveTrackingGearSummary(params: any, callback: TCallback<any>) {
    const hostPath = '/api/v1/live-tracking/gearsummary';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  getLastRwfEvent(params: any, callback: TCallback<any>) {
    const hostPath = '/api/v1/live-tracking/rwf-event/last';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  getRwfEventFeed(params: any, callback: TCallback<any>) {
    const hostPath = '/api/v1/live-tracking/rwf-event/feed';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  /**
   * isValidRWFAnalyzeGuilds
   */
  isValidRWFAnalyzeGuilds(guildsString: string, callback: TCallback<any>) {
    const hostPath = '/api/rwf-analyze/is-valid/guilds';
    const url = buildUrl(hostPath, { guildsString });
    PERFORM_GET(url, callback);
  },

  addKeystoneRunVideo(seasonSlug: string, run: string, params: any, callback: TCallback<any>) {
    const hostPath = `/api/mythic-plus-runs/videos/${seasonSlug}/${run}`;

    PERFORM_POST(hostPath, params, callback);
  },

  removeKeystoneRunVideo(seasonSlug: string, run: string, videoId: string, callback: TCallback<any>) {
    const hostPath = `/api/mythic-plus-runs/videos-remove/${seasonSlug}/${run}/${videoId}`;
    PERFORM_POST(hostPath, {}, callback);
  },

  addRaidEncounterVideo(params: any, callback: TCallback<any>) {
    const hostPath = '/api/guilds/boss-kills/videos';

    PERFORM_POST(hostPath, params, callback);
  },

  removeRaidEncounterVideo(videoId: string, callback: TCallback<any>) {
    const hostPath = `/api/guilds/boss-kills/videos-remove/${videoId}`;
    PERFORM_POST(hostPath, {}, callback);
  },

  viewLiveTrackingBossPulls(params: any, callback: TCallback<any>) {
    const hostPath = '/api/v1/live-tracking/bosspulls';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  viewLiveTrackingGuildIlvlHistory(params: any, callback: TCallback<any>) {
    const hostPath = '/api/v1/live-tracking/guild/ilvl-history';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  viewLiveTrackingGuildIlvlHistories(params: any, callback: TCallback<any>) {
    const hostPath = '/api/v1/live-tracking/guild/ilvl-history/batch';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  viewLiveTrackingGuildIlvlPulls(params: any, callback: TCallback<any>) {
    const hostPath = '/api/v1/live-tracking/guild/ilvl-history/pulls';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  generateKeystoneRunRoute(season: string, loggedRunId: number, callback: TCallback<any>) {
    const hostPath = `/api/mythic-plus/logged-runs/${season}/${loggedRunId}/route`;
    const url = buildUrl(hostPath, {});

    PERFORM_GET(url, callback);
  },

  impersonateUser(params: any, callback: TCallback<any>) {
    const hostPath = '/admin/user/impersonate';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  createClientImpersonationLaunch(params: any, callback: TCallback<any>) {
    const hostPath = '/admin/api/client-impersonation-launch';

    PERFORM_POST(hostPath, params, callback);
  },

  stopImpersonateUser(params: any, callback: TCallback<any>) {
    const hostPath = '/stop-impersonate';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  getPlunderstormLeaderboard(params: any, callback: TCallback<any>) {
    const hostPath = '/api/plunderstorm/leaderboard';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  getStatisticsData(params: IGetStatisticsDataQuery, callback: TCallback<TGetStatisticsData>) {
    const hostPath = '/api/statistics/get-data';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  getStatisticsDataById(id: number, callback: TCallback<TGetStatisticsData>) {
    const hostPath = `/api/statistics/get-data-by-id/${id}`;
    const url = buildUrl(hostPath, {});

    PERFORM_GET(url, callback);
  },

  getTierTalentsData(params: ITierTalentsDataParams, callback: TCallback<TGetTierTalentsData>) {
    const hostPath = '/api/statistics/tier/talents';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  getTierGearBySlotData(params: ITierGearDataParams, callback: TCallback<TTierGearBySlotResponse>) {
    const hostPath = '/api/statistics/tier/gear';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  getTierTrinketsData(params: ITierTrinketsDataParams, callback: TCallback<TTierTrinketsResponse>) {
    const hostPath = '/api/statistics/tier/trinkets';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  getSpecLoadoutTalentRuns(params: TSpecLoadoutTalentRunsParams, callback: TCallback<TSpecLoadoutTalentRunsResponse>) {
    const hostPath = '/api/spec-loadout/talents/runs';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  getSpecLoadoutTalentPovRuns(
    params: TSpecLoadoutTalentRunsParams,
    callback: TCallback<TSpecLoadoutTalentPovRunsResponse>
  ) {
    const hostPath = '/api/spec-loadout/talents/pov-runs';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  // timeline event management
  getTimelineEventList(params: TGetTimelineEventListParams, callback: TCallback<ITimelineEventSummary[]>) {
    const hostPath = '/admin/api/timeline-events';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  getTimelineEventDetailsById(id: number, callback: TCallback<ITimelineEventSummary>) {
    const hostPath = `/admin/api/timeline-events/${id}`;
    const url = buildUrl(hostPath, {});

    PERFORM_GET(url, callback);
  },

  createTimelineEvent(body: TCreateTimelineEvent, callback: TCallback<ITimelineEventSummary>) {
    const hostPath = '/admin/api/timeline-events';
    const url = buildUrl(hostPath, {});

    PERFORM_POST(url, body, callback);
  },

  updateTimelineEvent(id: number, body: TUpdateTimelineEvent, callback: TCallback<void>) {
    const hostPath = `/admin/api/timeline-events/${id}`;
    const url = buildUrl(hostPath, {});

    PERFORM_PUT(url, body, callback).catch((err) => {
      frontendLog.error({ err }, 'updateTimelineEvent request error');
      callback(err);
    });
  },

  deleteTimelineEvent(id: number, callback: TCallback<void>) {
    const hostPath = `/admin/api/timeline-events/${id}`;
    const url = buildUrl(hostPath, {});

    PERFORM_DELETE(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'deleteTimelineEvent request error');
      callback(err);
    });
  },

  getPublicTimelineEventList(params: TGetTimelineEventListParams, callback: TCallback<ITimelineEventSummary[]>) {
    const hostPath = '/api/timeline-events';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  // spells
  getSpells(params: TGetSpellsParams, callback: TCallback<TGetSpellsListResponse>) {
    const hostPath = '/api/spells/list';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  getGroupCompositionCompare(params: IGetGroupCompositionCompareParams, callback: TCallback<any>) {
    const hostPath = '/api/raiding/group-composition-compare';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  // character claim
  startCharacterClaimProcess(body: IStartInitialCharacterScanBody, callback: TCallback<any>) {
    const hostPath = '/api/character-claim/start-initial-character-scan';
    const url = buildUrl(hostPath, {});

    PERFORM_POST(url, body, callback);
  },

  requestCharacterClaimProcess(body: IRequestCharacterClaimProcessBody, callback: TCallback<any>) {
    const hostPath = '/api/character-claim/request';
    const url = buildUrl(hostPath, {});

    PERFORM_POST(url, body, callback);
  },

  startPendingCharacterClaimScan(body: IStartPendingCharacterClaimScanBody, callback: TCallback<any>) {
    const hostPath = '/api/character-claim/start-pending-character-claim-scan';
    const url = buildUrl(hostPath, {});

    PERFORM_POST(url, body, callback);
  },

  validateCharacterClaim(body: IValidateCharacterClaimProcessBody, callback: TCallback<any>) {
    const hostPath = '/api/character-claim/validate-character-claim';
    const url = buildUrl(hostPath, {});

    PERFORM_POST(url, body, callback);
  },

  deletePendingClaimValidation(params: { pendingClaimValidationId: number }, callback: TCallback<any>) {
    const hostPath = `/api/character-claim/pending-claim-validation/${params.pendingClaimValidationId}`;
    const url = buildUrl(hostPath, {});

    PERFORM_DELETE(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'deletePendingClaimValidation request error');
      callback(err);
    });
  },

  getDungeonEncounters(callback: TCallback<any>) {
    const hostPath = '/api/raiding/dungeon-encounters';
    const url = buildUrl(hostPath, {});

    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  getModerationReports(params: any, callback: TCallback<any>) {
    const hostPath = '/admin/api/moderation/reports';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  getModerationReport(id: number, callback: TCallback<any>) {
    const hostPath = `/admin/api/moderation/reports/${id}`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  getModerationReportByReferenceCode(referenceCode: string, callback: TCallback<any>) {
    const hostPath = `/admin/api/moderation/reports/by-reference/${encodeURIComponent(referenceCode)}`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  updateModerationReport(id: number, body: any, callback: TCallback<any>) {
    const hostPath = `/admin/api/moderation/reports/${id}`;
    const url = buildUrl(hostPath, {});
    PERFORM_PATCH(url, body, callback).catch((err) => {
      frontendLog.error({ err }, 'updateModerationReport request error');
      callback(err);
    });
  },

  batchUpdateModerationReports(body: any, callback: TCallback<any>) {
    const hostPath = '/admin/api/moderation/reports/batch';
    const url = buildUrl(hostPath, {});
    PERFORM_PATCH(url, body, callback).catch((err) => {
      frontendLog.error({ err }, 'batchUpdateModerationReports request error');
      callback(err);
    });
  },

  getTalentSemanticsFeedback(params: any, callback: TCallback<any>) {
    const hostPath = '/admin/api/talents/semantics/feedback';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  getTalentSemanticsFeedbackAggregate(params: any, callback: TCallback<any>) {
    const hostPath = '/admin/api/talents/semantics/feedback/aggregate';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  updateTalentSemanticsFeedback(id: number, body: any, callback: TCallback<any>) {
    const hostPath = `/admin/api/talents/semantics/feedback/${id}`;
    const url = buildUrl(hostPath, {});
    PERFORM_PATCH(url, body, callback).catch((err) => {
      frontendLog.error({ err }, 'updateTalentSemanticsFeedback request error');
      callback(err);
    });
  },

  batchUpdateTalentSemanticsFeedback(body: any, callback: TCallback<any>) {
    const hostPath = '/admin/api/talents/semantics/feedback/batch';
    const url = buildUrl(hostPath, {});
    PERFORM_PATCH(url, body, callback).catch((err) => {
      frontendLog.error({ err }, 'batchUpdateTalentSemanticsFeedback request error');
      callback(err);
    });
  },

  getModerationReportAuditLogs(reportId: number, callback: TCallback<any>) {
    const hostPath = `/admin/api/moderation/reports/${reportId}/audit-logs`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  getReporterBans(callback: TCallback<any>) {
    const hostPath = '/admin/api/moderation/reporter-bans';
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  banReporter(body: any, callback: TCallback<any>) {
    const hostPath = '/admin/api/moderation/reporter-bans';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, body, callback).catch((err) => {
      frontendLog.error({ err }, 'banReporter request error');
      callback(err);
    });
  },

  unbanReporter(banId: number, callback: TCallback<any>) {
    const hostPath = `/admin/api/moderation/reporter-bans/${banId}`;
    const url = buildUrl(hostPath, {});
    PERFORM_DELETE(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'unbanReporter request error');
      callback(err);
    });
  }
};

type TApiServiceKeys = keyof typeof apiService;

type TApiServiceAsync = {
  [key in TApiServiceKeys]: (...args: any) => Promise<any>;
};

export const apiServiceAsync = makeApiServiceAsync(apiService) as TApiServiceAsync;
export const retryableApiServiceAsync = makeRetryableApiServiceAsync(apiServiceAsync) as TApiServiceAsync;

export default apiService;
